import { useState } from 'react';
import {
    Box,
    Chip,
    Grid,
    IconButton,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Typography,
    styled,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import KeyIcon from '@mui/icons-material/Key';
import LanguageIcon from '@mui/icons-material/Language';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import Tooltip from 'components/popups/Tooltip';
import moment from 'moment-timezone';
import { getRelativeTime } from 'utility/dates';
import { abbreviateNumber, formatPrice } from 'utility/numbers';
import { getSalesStatus, SALES_STATUS } from '../productFilters';

export default function BundlesTable({ event, bundles, compact = false, onBundleSelect }) {
    const [menu, setMenu] = useState({ anchorEl: null, bundleId: null });

    const handleMenuOpen = (event, bundleId) => {
        setMenu({ anchorEl: event.currentTarget, bundleId });
    };

    const handleMenuClose = () => {
        setMenu({ anchorEl: null, bundleId: null });
    };

    const handleEdit = () => {
        onBundleSelect(menu.bundleId);
        handleMenuClose();
    };

    return (
        <Box>
            {bundles.length === 0 && (
                <EmptyState elevation={0}>
                    <Typography>No bundles found</Typography>
                </EmptyState>
            )}

            {bundles.map((bundle) => (
                <BundleRow key={bundle.bundle_id} container>
                    <Grid item xs={12} sm={12} lg={8} sx={{ minWidth: 0 }}>
                        <BundlesTableTitle
                            bundle={bundle}
                            event={event}
                            compact={compact}
                            onEdit={() => onBundleSelect(bundle.bundle_id)}
                        />
                    </Grid>

                    <Grid item xs={8} sm={8} lg={2}>
                        <MetricCell
                            label="ONLINE"
                            text={bundle.price === 0 ? 'FREE' : formatPrice(bundle.price, { minimumFractionDigits: 0 })}
                        />
                    </Grid>

                    <Grid item xs={3} sm={2} lg={1}>
                        <MetricCell
                            label="SOLD"
                            text={`${abbreviateNumber(bundle.qty_sold, 0, 1, true)} / ${abbreviateNumber(bundle.total_available, 0, 1, true)}`}
                        />
                    </Grid>

                    <Grid item xs={1} sm={2} lg={1}>
                        <Box display="flex" justifyContent="flex-end">
                            <IconButton
                                onClick={(event) => handleMenuOpen(event, bundle.bundle_id)}
                                size="small"
                            >
                                <MoreVertIcon />
                            </IconButton>
                        </Box>
                    </Grid>
                </BundleRow>
            ))}

            <Menu anchorEl={menu.anchorEl} open={Boolean(menu.anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={handleEdit}>Edit</MenuItem>
            </Menu>
        </Box>
    );
}

function MetricCell({ label, text }) {
    return (
        <Box display="flex" flexDirection="column" alignItems="flex-end">
            <Typography fontSize={12} variant="caption">
                {label}
            </Typography>
            <Typography fontSize={14} fontWeight="bold" textAlign="end">
                {text}
            </Typography>
        </Box>
    );
}

function BundlesTableTitle({ bundle, compact, event, onEdit }) {
    const updatedAt = bundle.updated_at
        ? `Edited ${getRelativeTime(bundle.updated_at, 'short')}`
        : null;

    let hoverMessage = null;

    if (bundle.updated_at && bundle.updated_by) {
        hoverMessage = `Last edited by ${bundle.updated_by} on ${moment(bundle.updated_at).format('dddd, MMMM D, h:mma')}`;
    }

    return (
        <Box mb={[2, 2, 2, 0]}>
            <Stack direction="row" spacing={1.25} alignItems="center" useFlexGap flexWrap="wrap">
                <TitleText onClick={onEdit ?? undefined}>{bundle.bundle_name}</TitleText>
                {!compact && (<>
                    <TypePill label={'BUNDLE'} size="small" />
                    {updatedAt && (
                        <Tooltip message={hoverMessage}>
                            <Typography variant="caption" color="text.secondary">
                                {updatedAt}
                            </Typography>
                        </Tooltip>
                    )}
                </>)}
            </Stack>

            {!compact && (
                <>
                    <Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap" mt={1.5}>
                        <AudiencePill bundle={bundle} />
                        <SaleStatusPill
                            startDate={bundle.sale_start_date}
                            endDate={bundle.sale_end_date}
                            timezone={event?.time_zone}
                            qtyLeft={bundle.qty_left}
                            forceGray={bundle.visible === 0}
                        />
                    </Stack>

                    {Boolean(bundle.prod_group_name) && (
                        <Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap" mt={1}>
                            <GroupPill name={bundle.prod_group_name} />
                        </Stack>
                    )}
                </>
            )}
        </Box>
    );
}

function GroupPill({ name }) {
    if (name == null || name === '') {
        return null;
    }

    return (
        <Tooltip message={`Ticket Group: ${name}`}>
            <MetaPill icon={<FolderOutlinedIcon />} label={name} pillcolor="group" />
        </Tooltip>
    );
}

function SaleStatusPill({ startDate, endDate, timezone, qtyLeft, forceGray = false }) {
    const now = moment();
    const saleStart = startDate ? moment(startDate).tz(timezone, true) : null;
    const saleEnd = endDate ? moment(endDate).tz(timezone, true) : null;
    const salesStatus = getSalesStatus(
        { sale_start_date: startDate, sale_end_date: endDate, qty_left: qtyLeft },
        timezone,
        now,
    );

    if (!salesStatus) {
        return null;
    }

    if (salesStatus === SALES_STATUS.soldOut) {
        return (
            <Tooltip
                message={`Sold out: Sales were scheduled to end ${saleEnd.format('dddd, MMMM D')} at ${saleEnd.format('hh:mm a')}`}
            >
                <MetaPill icon={<AccessTimeIcon />} label="Sold Out" pillcolor={forceGray ? 'gray' : 'red'} />
            </Tooltip>
        );
    }

    if (saleStart > now) {
        return (
            <Tooltip
                message={`Sales start ${saleStart.format('dddd, MMMM D')} at ${saleStart.format('hh:mm a')}`}
            >
                <MetaPill
                    icon={<AccessTimeIcon />}
                    label={`Starts ${saleStart.format('MMM D')} • ${saleStart.format('hh:mm a')}`}
                    pillcolor={forceGray ? 'gray' : 'blue'}
                />
            </Tooltip>
        );
    }

    if (saleEnd < now) {
        return (
            <Tooltip
                message={`Sales ended ${saleEnd.format('dddd, MMMM D')} at ${saleEnd.format('hh:mm a')}`}
            >
                <MetaPill icon={<AccessTimeIcon />} label="Sale Ended" pillcolor="gray" />
            </Tooltip>
        );
    }

    return (
        <Tooltip
            message={`Sales end ${saleEnd.format('dddd, MMMM D')} at ${saleEnd.format('hh:mm a')}`}
        >
            <MetaPill
                icon={<AccessTimeIcon />}
                label={`On Sale • ${getRelativeTime(saleEnd, 'short')} left`}
                pillcolor={forceGray ? 'gray' : 'green'}
            />
        </Tooltip>
    );
}

function AudiencePill({ bundle }) {
    if (bundle.access_code) {
        return (
            <Tooltip
                message={`Buyer must enter access code: ${bundle.access_code} to purchase this bundle`}
            >
                <MetaPill icon={<KeyIcon />} label="Access Code" pillcolor="gold" />
            </Tooltip>
        );
    }

    if (bundle.visible === 1) {
        return (
            <Tooltip message="This bundle is available to anyone">
                <MetaPill icon={<LanguageIcon />} label="Everyone" pillcolor="green" />
            </Tooltip>
        );
    }

    return (
        <Tooltip message="This bundle is not available for online purchase">
            <MetaPill icon={<VisibilityOffIcon />} label="Offline" pillcolor="gray" />
        </Tooltip>
    );
}


const EmptyState = styled(Paper)(({ theme }) => ({
    minHeight: 128,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(2),
}));

const BundleRow = styled(Grid)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: '#fff',
    alignItems: 'flex-start',
    borderBottomWidth: 0.5,
    borderBottomColor: theme.palette.grey[300],
    borderBottomStyle: 'solid',
}));

const TitleText = styled(Typography)(({ theme }) => ({
    paddingRight: theme.spacing(0.5),
    display: 'inline',
    fontSize: 16,
    lineHeight: 1.25,
    fontWeight: theme.typography.fontWeightBold,
    '&:hover': {
        cursor: 'pointer',
    },
}));

const TypePill = styled(Chip)(({ theme }) => ({
    height: 20,
    borderRadius: 6,
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[300]}`,
    '& .MuiChip-label': {
        fontSize: 12,
        fontWeight: theme.typography.fontWeightMedium,
        letterSpacing: '0.08em',
        color: theme.palette.grey[700],
    },
}));


const MetaPill = styled(Chip, {
    shouldForwardProp: (prop) => prop !== 'pillcolor',
})(({ theme, pillcolor }) => {
    const styles = {
        green: {
            backgroundColor: '#DDF5E6',
            color: '#0F5132',
        },
        blue: {
            backgroundColor: '#DCEEFF',
            color: '#0B4F86',
        },
        gold: {
            backgroundColor: '#FDF1CC',
            color: '#8A5A00',
        },
        red: {
            backgroundColor: '#F9E8E4',
            color: '#B01212',
        },
        gray: {
            backgroundColor: theme.palette.grey[200],
            color: theme.palette.grey[700],
        },
        group: {
            backgroundColor: '#DFE9FF',
            color: '#234A8F',
        },
    };

    return {
        height: 20,
        borderRadius: 12,
        backgroundColor: styles[pillcolor]?.backgroundColor,
        color: styles[pillcolor]?.color,
        '& .MuiChip-icon': {
            color: 'inherit',
            fontSize: 12,
        },
        '& .MuiChip-label': {
            fontSize: 12,
            fontWeight: theme.typography.fontWeightMedium,
        },
    };
});
